import { useLingui } from "@lingui/react";
import { isPrivateAdvertiser } from "@mobility-item-page/utilities";
import { ElementType, Intent, ObjectPosition, TargetType } from "@mobility-item-page/utilities/client";
import { Button } from "@warp-ds/react";
import { useAdViewAd, useAdViewMeta } from "src/context/AdViewProviderContext.tsx";
import { useAppConfig } from "src/context/AppConfigProviderContext.tsx";
import usePulse from "../../../utils/pulse-tracking/usePulse.ts";
import { Icon } from "./../Icon.tsx";
import GoogleMaps from "./GoogleMaps.tsx";
import HiddenAddressInfo from "./HiddenAddressInfo.tsx";
import Location from "./Location.tsx";

const LocationSection = () => {
  const { i18n } = useLingui();
  const { location } = useAdViewAd();
  const { ownerUrn } = useAdViewMeta();
  const { hideDetailedAddress, isLoggedIn, googleMapsEmbed } = useAppConfig();

  const { trackClick } = usePulse();
  const isPrivateUser = isPrivateAdvertiser(ownerUrn);

  const streetAddress = location?.streetAddress;
  const postalOrCountry = location?.postalName ? location?.postalName : (location?.countryName ?? "");
  const shortAddress = `${location?.postalCode} ${postalOrCountry}`;
  const fullAddress = streetAddress ? `${streetAddress}, ${shortAddress}` : shortAddress;
  const hasShortAddress = location?.postalCode?.length && postalOrCountry?.length > 0;

  const getMapImage = () => {
    const image = location?.position?.mapImage?.replace("size=400x300", "size=800x400");
    return isPrivateUser && hideDetailedAddress ? image?.replace("&showPin=true", "") : image;
  };

  const mapHref = googleMapsEmbed
    ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(fullAddress)}`
    : location?.position?.links?.finnvector?.uri;
  const mapImage = getMapImage();
  const hasMap = mapHref && mapImage;

  if (!location || !hasShortAddress) {
    return null;
  }

  const handleOnClickTracking = () => {
    // Tracking event: CE_ev54
    trackClick({
      name: "Go to map",
      objectId: "CE_ev54",
      elementType: ElementType.Link,
      intent: Intent.Open,
      position: ObjectPosition.AboveMap,
      target: {
        id: mapHref || "",
        type: googleMapsEmbed ? TargetType.ExternalContent : TargetType.Page,
      },
    });
  };

  const locationText = i18n.t({
    id: "itemPageLayout.locationSection.location.title",
    message: "Location",
    comment: "Title for location section",
  });

  const getAddressText = () => {
    if (!isPrivateUser) {
      return hasMap ? (
        <Button
          link
          href={mapHref}
          onClick={handleOnClickTracking}
          target={googleMapsEmbed ? "_blank" : "_self"}
          className="flex items-center gap-4"
        >
          {fullAddress}
          {googleMapsEmbed && <Icon name="LinkExternal" />}
        </Button>
      ) : (
        <p className="mb-0">{fullAddress}</p>
      );
    }

    if (!streetAddress) {
      return hasMap ? (
        <Button
          link
          href={mapHref}
          onClick={handleOnClickTracking}
          target={googleMapsEmbed ? "_blank" : "_self"}
          className="flex items-center gap-4"
        >
          {shortAddress}
          {googleMapsEmbed && <Icon name="LinkExternal" />}
        </Button>
      ) : (
        <p className="mb-0">{shortAddress}</p>
      );
    }

    if (hideDetailedAddress || !isLoggedIn) {
      return (
        <p className="mb-0">
          {shortAddress}
          <HiddenAddressInfo />
        </p>
      );
    }

    if (!hideDetailedAddress) {
      return hasMap ? (
        <Button
          link
          href={mapHref}
          onClick={handleOnClickTracking}
          target={googleMapsEmbed ? "_blank" : "_self"}
          className="flex items-center gap-4"
        >
          {streetAddress}, {shortAddress}
          {googleMapsEmbed && <Icon name="LinkExternal" />}
        </Button>
      ) : (
        <p className="mb-0">
          {streetAddress}, {shortAddress}
        </p>
      );
    }
    return null;
  };

  const addressText = getAddressText();
  if (!addressText && !googleMapsEmbed) return null;

  return (
    <div className="mt-40 pt-40 border-t">
      <h2 className="t3 mb-16">{locationText}</h2>

      <div className="flex items-center gap-4">
        <Icon name="PinMarker" />
        {addressText}
      </div>

      {hasMap && !googleMapsEmbed && (
        <Location
          mapHref={mapHref}
          mapImage={mapImage}
          hideAddressLink={isPrivateUser && !!streetAddress && hideDetailedAddress}
        />
      )}
      {googleMapsEmbed && <GoogleMaps mapsEmbed={googleMapsEmbed} address={fullAddress} />}
    </div>
  );
};

export default LocationSection;
